(() => {
  if (window.__mainBlogAjaxInitialized) return;
  window.__mainBlogAjaxInitialized = true;

  const SECTION_SELECTOR = '[data-main-blog-section]';
  const AJAX_LINK_SELECTOR = [
    '.main-blog-figma__pagination a.main-blog-figma__pagination-link',
    '.main-blog-figma__pagination a.main-blog-figma__pagination-index'
  ].join(', ');
  const TAG_LINK_SELECTOR = '.main-blog-figma__tags a.main-blog-figma__tag';
  const SORT_FORM_SELECTOR = '[data-blog-sort-form]';
  const SORT_TOGGLE_SELECTOR = '[data-blog-sort-toggle]';
  const SORT_PANEL_SELECTOR = '[data-blog-sort-panel]';
  const SORT_OPTION_SELECTOR = '[data-blog-sort-option]';
  const ARTICLE_CARD_SELECTOR = '[data-blog-article-card]';
  const ARTICLE_CARD_INTERACTIVE_SELECTOR = [
    'a',
    'button',
    'input',
    'select',
    'textarea',
    'label',
    'summary',
    '[role="button"]',
    '[role="link"]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');
  const SOURCE_CONTENT_SELECTOR = '[data-blog-source-content]';
  const FILTERED_CONTENT_SELECTOR = '[data-blog-filtered-content]';
  const SHOWING_VALUE_SELECTOR = '[data-blog-showing-value]';
  const PAGINATION_SELECTOR = '.main-blog-figma__pagination';
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

  const requestControllers = new WeakMap();

  const getSectionId = (section) => {
    if (!section) return '';

    const explicitId = section.getAttribute('data-section-id');
    if (explicitId) return explicitId;

    const host = section.closest('[id^="shopify-section-"]');
    if (!host) return '';

    return host.id.replace('shopify-section-', '');
  };

  const buildSectionRequestUrl = (rawUrl, sectionId) => {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url;
  };

  const buildHistoryUrl = (rawUrl) => {
    const url = new URL(rawUrl, window.location.origin);
    url.searchParams.delete('section_id');
    return `${url.pathname}${url.search}${url.hash}`;
  };

  const parseSectionFromHtml = (html) => {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(html, 'text/html');
    return parsedDocument.querySelector(SECTION_SELECTOR);
  };

  const getSelectedTagsFromUrl = () => {
    const params = new URL(window.location.href).searchParams;
    const rawTags = params.get('blog_tags');
    if (!rawTags) return [];

    return rawTags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  };

  const closeBlogSort = (form) => {
    if (!form) return;

    const toggle = form.querySelector(SORT_TOGGLE_SELECTOR);
    const panel = form.querySelector(SORT_PANEL_SELECTOR);

    form.classList.remove('is-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    if (panel) panel.hidden = true;
  };

  const closeAllBlogSorts = (exceptForm = null) => {
    document.querySelectorAll(SORT_FORM_SELECTOR).forEach((form) => {
      if (form !== exceptForm) closeBlogSort(form);
    });
  };

  const toggleBlogSort = (form) => {
    if (!form) return;

    const toggle = form.querySelector(SORT_TOGGLE_SELECTOR);
    const panel = form.querySelector(SORT_PANEL_SELECTOR);
    const isOpen = form.classList.contains('is-open');

    closeAllBlogSorts(form);
    form.classList.toggle('is-open', !isOpen);
    if (toggle) toggle.setAttribute('aria-expanded', String(!isOpen));
    if (panel) panel.hidden = isOpen;
  };

  const getCardTags = (card) =>
    (card.getAttribute('data-blog-tags') || '')
      .split(/\s+/)
      .map((tag) => tag.trim())
      .filter(Boolean);

  const getSourceArticles = (section) => [
    ...(section.querySelector(SOURCE_CONTENT_SELECTOR)?.querySelectorAll(ARTICLE_CARD_SELECTOR) || [])
  ];

  const getMatchingArticles = (section, selectedTags) => {
    const articles = getSourceArticles(section);
    if (selectedTags.length === 0) return articles;

    return articles.filter((article) => {
      const articleTags = getCardTags(article);
      return selectedTags.some((tag) => articleTags.includes(tag));
    });
  };

  const getArticleData = (article) => ({
    title: article.getAttribute('data-blog-title') || '',
    url: article.getAttribute('data-blog-url') || '#',
    tags: getCardTags(article),
    excerptShort: article.getAttribute('data-blog-excerpt-short') || '',
    excerptLong: article.getAttribute('data-blog-excerpt-long') || '',
    tagLabel: article.getAttribute('data-blog-tag-label') || '',
    date: article.getAttribute('data-blog-date') || '',
    imageSrc: article.getAttribute('data-blog-image-src') || '',
    imageSrcset: article.getAttribute('data-blog-image-srcset') || '',
    imageAlt: article.getAttribute('data-blog-image-alt') || '',
    imageWidth: article.getAttribute('data-blog-image-width') || '',
    imageHeight: article.getAttribute('data-blog-image-height') || ''
  });

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return `${text.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
  };

  const createElement = (tagName, className, text = null) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== null) element.textContent = text;
    return element;
  };

  const copyArticleDataAttributes = (element, article) => {
    element.setAttribute('data-blog-article-card', '');
    element.setAttribute('data-blog-title', article.title);
    element.setAttribute('data-blog-url', article.url);
    element.setAttribute('data-blog-tags', article.tags.join(' '));
  };

  const appendArticleImage = (link, article, className, sizes) => {
    if (!article.imageSrc) return;

    const image = document.createElement('img');
    if (article.imageSrcset) image.setAttribute('srcset', article.imageSrcset);
    image.src = article.imageSrc;
    image.sizes = sizes;
    image.alt = article.imageAlt || article.title;
    image.loading = 'lazy';
    if (article.imageWidth) image.width = Number(article.imageWidth);
    if (article.imageHeight) image.height = Number(article.imageHeight);
    image.className = className;
    link.append(image);
  };

  const createArticleTitle = (tagName, className, article, maxLength = null) => {
    const title = createElement(tagName, className);
    const link = document.createElement('a');
    link.href = article.url;
    link.textContent = maxLength ? truncateText(article.title, maxLength) : article.title;
    title.append(link);
    return title;
  };

  const getArticleMeta = (article) => {
    if (article.tagLabel && article.date) return `${article.tagLabel} | ${article.date}`;
    return article.tagLabel || article.date;
  };

  const getReadMoreText = (section) =>
    section.getAttribute('data-read-article-label') ||
    section.querySelector('.kh-card__read-more, .kh-feature-card__read-more')?.textContent.trim() ||
    '';

  const createStandardCard = (article, { overlay = false, compact = false } = {}, readMoreText = '') => {
    const card = createElement('article', `kh-card${overlay ? ' kh-card--overlay' : ''}${compact ? ' kh-card--compact' : ''}`);
    copyArticleDataAttributes(card, article);

    const mediaLink = createElement('a', 'kh-card__media-link');
    mediaLink.href = article.url;
    appendArticleImage(mediaLink, article, 'kh-card__image', '(min-width: 990px) calc((100vw - 22rem) / 3), 100vw');

    const panel = createElement('div', 'kh-card__panel');
    panel.append(
      createArticleTitle('h2', 'kh-card__title', article),
      createElement('p', 'kh-card__excerpt', article.excerptShort),
      createElement('div', 'kh-card__divider'),
      createElement('p', 'kh-card__meta', getArticleMeta(article))
    );

    const readMore = createElement('a', 'kh-card__read-more', readMoreText);
    readMore.href = article.url;
    panel.append(readMore);

    card.append(mediaLink, panel);
    return card;
  };

  const createFeatureCard = (article, { right = false } = {}, readMoreText = '') => {
    const card = createElement('article', `kh-feature-card kh-feature-card--${right ? 'right' : 'left'}`);
    copyArticleDataAttributes(card, article);

    const mediaLink = createElement('a', 'kh-feature-card__media-link');
    mediaLink.href = article.url;
    appendArticleImage(mediaLink, article, 'kh-feature-card__image', '(min-width: 990px) 41.2rem, 100vw');

    const body = createElement('div', 'kh-feature-card__body');
    body.append(
      createArticleTitle('h2', 'kh-feature-card__title', article),
      createElement('p', 'kh-feature-card__excerpt', article.excerptLong),
      createElement('div', 'kh-feature-card__divider'),
      createElement('p', 'kh-feature-card__meta', getArticleMeta(article))
    );

    const readMore = createElement('a', 'kh-feature-card__read-more', readMoreText);
    readMore.href = article.url;
    body.append(readMore);

    card.append(mediaLink, body);
    return card;
  };

  const createMiniCard = (article) => {
    const card = createElement('article', 'kh-mini-card');
    copyArticleDataAttributes(card, article);

    const mediaLink = createElement('a', 'kh-mini-card__media-link');
    mediaLink.href = article.url;
    appendArticleImage(mediaLink, article, 'kh-mini-card__image', '(min-width: 990px) 8.6rem, 8.6rem');

    const panel = createElement('div', 'kh-mini-card__panel');
    panel.append(
      createArticleTitle('h3', 'kh-mini-card__title', article, 34),
      createElement('div', 'kh-mini-card__divider'),
      createElement('p', 'kh-mini-card__meta', getArticleMeta(article))
    );

    card.append(mediaLink, panel);
    return card;
  };

  const buildFilteredLayout = (section, matchingArticles) => {
    const filteredContent = section.querySelector(FILTERED_CONTENT_SELECTOR);
    if (!filteredContent) return;

    const readMoreText = getReadMoreText(section);
    const articles = matchingArticles.map(getArticleData);
    filteredContent.replaceChildren();

    if (articles.length > 0) {
      const topRow = createElement('div', 'main-blog-figma__row main-blog-figma__row--top');
      articles.slice(0, 3).forEach((article, index) => {
        topRow.append(createStandardCard(article, { overlay: index === 1 }, readMoreText));
      });
      filteredContent.append(topRow);
    }

    if (articles.length > 3) {
      const featureLeftRow = createElement(
        'div',
        `main-blog-figma__row main-blog-figma__row--feature-left${articles.length <= 4 ? ' main-blog-figma__row--feature-left-single' : ''}`
      );
      featureLeftRow.append(createFeatureCard(articles[3], { right: false }, readMoreText));

      if (articles.length > 4) {
        const miniList = createElement('div', 'kh-mini-list');
        articles.slice(4, 7).forEach((article) => {
          miniList.append(createMiniCard(article));
        });
        featureLeftRow.append(miniList);
      }

      filteredContent.append(featureLeftRow);
    }

    if (articles.length > 7) {
      const featureRightRow = createElement('div', 'main-blog-figma__row main-blog-figma__row--feature-right');
      featureRightRow.append(createStandardCard(articles[7], { compact: true }, readMoreText));
      if (articles.length > 8) {
        featureRightRow.append(createFeatureCard(articles[8], { right: true }, readMoreText));
      }
      filteredContent.append(featureRightRow);
    }

    if (articles.length > 9) {
      const moreRow = createElement('div', 'main-blog-figma__row main-blog-figma__row--more');
      articles.slice(9).forEach((article) => {
        moreRow.append(createStandardCard(article, {}, readMoreText));
      });
      filteredContent.append(moreRow);
    }
  };

  const updateBlogHistory = (selectedTags) => {
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.delete('page');

    if (selectedTags.length > 0) {
      nextUrl.searchParams.set('blog_tags', selectedTags.join(','));
    } else {
      nextUrl.searchParams.delete('blog_tags');
    }

    window.history.pushState({ mainBlogAjax: true }, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
  };

  const updateShowingValue = (section, selectedTags, matchingCount) => {
    const showingValue = section.querySelector(SHOWING_VALUE_SELECTOR);
    if (!showingValue) return;

    if (selectedTags.length === 0) {
      showingValue.textContent = showingValue.getAttribute('data-default-value') || showingValue.textContent;
      return;
    }

    const ofLabel = showingValue.getAttribute('data-of-label') || 'of';
    const resultsLabel = showingValue.getAttribute('data-results-label') || 'articles';
    const from = matchingCount > 0 ? 1 : 0;
    const to = matchingCount;
    showingValue.textContent = `${from}-${to} ${ofLabel} ${matchingCount} ${resultsLabel}`;
  };

  const updateTagStates = (section, selectedTags) => {
    section.querySelectorAll(TAG_LINK_SELECTOR).forEach((tagLink) => {
      const tag = tagLink.getAttribute('data-blog-tag');
      const isAll = tagLink.hasAttribute('data-blog-tag-all');
      tagLink.classList.toggle('is-active', isAll ? selectedTags.length === 0 : selectedTags.includes(tag));
    });
  };

  const updatePaginationVisibility = (section, selectedTags) => {
    const pagination = section.querySelector(PAGINATION_SELECTOR);
    if (!pagination) return;

    pagination.hidden = selectedTags.length > 0;
  };

  const applyBlogFilters = (section, selectedTags, { pushHistory = false } = {}) => {
    const normalizedTags = [...new Set(selectedTags.filter(Boolean))];
    const matchingArticles = getMatchingArticles(section, normalizedTags);
    const sourceContent = section.querySelector(SOURCE_CONTENT_SELECTOR);
    const filteredContent = section.querySelector(FILTERED_CONTENT_SELECTOR);

    if (normalizedTags.length === 0) {
      if (sourceContent) sourceContent.hidden = false;
      if (filteredContent) {
        filteredContent.hidden = true;
        filteredContent.replaceChildren();
      }
    } else {
      buildFilteredLayout(section, matchingArticles);
      if (sourceContent) sourceContent.hidden = true;
      if (filteredContent) filteredContent.hidden = false;
    }

    updateTagStates(section, normalizedTags);
    updateShowingValue(section, normalizedTags, matchingArticles.length);
    updatePaginationVisibility(section, normalizedTags);

    if (pushHistory) updateBlogHistory(normalizedTags);
  };

  const getNextSelectedTags = (section, clickedTagLink) => {
    if (clickedTagLink.hasAttribute('data-blog-tag-all')) return [];

    const clickedTag = clickedTagLink.getAttribute('data-blog-tag');
    if (!clickedTag) return [];

    const activeTags = [...section.querySelectorAll(`${TAG_LINK_SELECTOR}.is-active[data-blog-tag]`)]
      .map((tagLink) => tagLink.getAttribute('data-blog-tag'))
      .filter(Boolean);
    const selectedTags = new Set(activeTags);

    if (selectedTags.has(clickedTag)) {
      selectedTags.delete(clickedTag);
    } else {
      selectedTags.add(clickedTag);
    }

    return [...selectedTags];
  };

  const initializeBlogSection = (section) => {
    if (!section) return;

    closeAllBlogSorts();
    applyBlogFilters(section, getSelectedTagsFromUrl(), { pushHistory: false });
  };

  const abortSectionRequest = (section) => {
    const activeController = requestControllers.get(section);
    if (!activeController) return;

    activeController.abort();
    requestControllers.delete(section);
  };

  const setSectionLoadingState = (section, isLoading) => {
    section.classList.toggle('is-loading', isLoading);
    section.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  };

  const getStickyHeaderOffset = () => {
    const stickyHeader = document.querySelector('sticky-header');
    if (!stickyHeader) return 0;
    if (!stickyHeader.classList.contains('shopify-section-header-sticky')) return 0;
    return stickyHeader.offsetHeight || 0;
  };

  const scrollToToolbar = (section) => {
    const toolbar = section.querySelector('.main-blog-figma__toolbar');
    const target = toolbar || section;
    const targetTop = target.getBoundingClientRect().top + window.scrollY;
    const stickyOffset = getStickyHeaderOffset();
    const topOffset = 8;
    const scrollTop = Math.max(0, targetTop - stickyOffset - topOffset);

    window.scrollTo({
      top: scrollTop,
      behavior: REDUCED_MOTION_QUERY.matches ? 'auto' : 'smooth'
    });
  };

  const shouldHandleAjaxLink = (event, link, section) => {
    if (!link || !section) return false;
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target !== '_self') return false;
    if (link.hasAttribute('download')) return false;

    const href = link.getAttribute('href');
    if (!href || href.startsWith('#')) return false;

    const destination = new URL(link.href, window.location.origin);
    if (destination.origin !== window.location.origin) return false;

    return Boolean(getSectionId(section));
  };

  const refreshSection = async (section, nextUrl, { pushHistory = true, replaceHistory = false } = {}) => {
    const sectionId = getSectionId(section);
    if (!sectionId) {
      window.location.assign(nextUrl.toString());
      return;
    }

    abortSectionRequest(section);

    const requestController = new AbortController();
    requestControllers.set(section, requestController);

    try {
      setSectionLoadingState(section, true);

      const requestUrl = buildSectionRequestUrl(nextUrl, sectionId);
      const response = await fetch(requestUrl.toString(), {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        signal: requestController.signal
      });

      if (!response.ok) {
        throw new Error(`Main blog request failed: ${response.status}`);
      }

      const responseHtml = await response.text();
      const nextSection = parseSectionFromHtml(responseHtml);
      if (!nextSection) {
        throw new Error('Main blog section not found in response');
      }

      section.innerHTML = nextSection.innerHTML;
      initializeBlogSection(section);
      scrollToToolbar(section);
      section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));

      if (pushHistory) {
        const historyUrl = buildHistoryUrl(nextUrl);
        if (replaceHistory) {
          window.history.replaceState({ mainBlogAjax: true }, '', historyUrl);
        } else {
          window.history.pushState({ mainBlogAjax: true }, '', historyUrl);
        }
      }
    } catch (error) {
      if (error?.name === 'AbortError') return;
      window.location.assign(nextUrl.toString());
    } finally {
      if (requestControllers.get(section) === requestController) {
        requestControllers.delete(section);
      }
      setSectionLoadingState(section, false);
    }
  };

  document.addEventListener('click', (event) => {
    const sortToggle = event.target.closest(SORT_TOGGLE_SELECTOR);
    if (sortToggle) {
      event.preventDefault();
      toggleBlogSort(sortToggle.closest(SORT_FORM_SELECTOR));
      return;
    }

    const sortOption = event.target.closest(SORT_OPTION_SELECTOR);
    if (sortOption) {
      event.preventDefault();

      const sortForm = sortOption.closest(SORT_FORM_SELECTOR);
      const currentLabel = sortForm?.querySelector('[data-blog-sort-current]');
      if (currentLabel) {
        currentLabel.textContent = sortOption.getAttribute('data-sort-label') || sortOption.textContent.trim();
      }

      sortForm?.querySelectorAll(SORT_OPTION_SELECTOR).forEach((option) => {
        option.classList.toggle('is-active', option === sortOption);
      });
      closeBlogSort(sortForm);
      return;
    }

    const tagLink = event.target.closest(TAG_LINK_SELECTOR);
    if (tagLink) {
      const section = tagLink.closest(SECTION_SELECTOR);
      if (!section) return;

      event.preventDefault();
      closeAllBlogSorts();
      applyBlogFilters(section, getNextSelectedTags(section, tagLink), { pushHistory: true });
      return;
    }

    const articleCard = event.target.closest(ARTICLE_CARD_SELECTOR);
    if (articleCard && !event.target.closest(ARTICLE_CARD_INTERACTIVE_SELECTOR)) {
      const articleUrl = articleCard.getAttribute('data-blog-url');
      if (articleUrl && articleUrl !== '#') {
        if (event.metaKey || event.ctrlKey) {
          window.open(articleUrl, '_blank', 'noopener');
        } else {
          window.location.assign(articleUrl);
        }
      }
      return;
    }

    if (!event.target.closest(SORT_FORM_SELECTOR)) {
      closeAllBlogSorts();
    }

    const ajaxLink = event.target.closest(AJAX_LINK_SELECTOR);
    if (!ajaxLink) return;

    const section = ajaxLink.closest(SECTION_SELECTOR);
    if (!shouldHandleAjaxLink(event, ajaxLink, section)) return;

    event.preventDefault();
    refreshSection(section, new URL(ajaxLink.href, window.location.origin), { pushHistory: true });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeAllBlogSorts();
  });

  document.querySelectorAll(SECTION_SELECTOR).forEach(initializeBlogSection);

  window.addEventListener('popstate', () => {
    const section = document.querySelector(SECTION_SELECTOR);
    if (!section || !getSectionId(section)) return;

    refreshSection(section, new URL(window.location.href), {
      pushHistory: false,
      replaceHistory: true
    });
  });
})();
